import { useMemo } from 'react';
import Fuse from 'fuse.js';

export interface FuseSearchOptions {
  threshold?: number;
  ignoreLocation?: boolean;
}

/**
 * Fuzzy-filter a small local list (~50 entries) with Fuse.js.
 * Returns the original list when the query is empty.
 */
export function useFuseSearch<T>(
  list: T[],
  keys: (string | { name: string; weight: number })[],
  query: string,
  options?: FuseSearchOptions
): T[] {
  const fuse = useMemo(
    () =>
      new Fuse(list, {
        keys: keys as any,
        threshold: options?.threshold ?? 0.4,
        ignoreLocation: options?.ignoreLocation ?? true,
        includeScore: false,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(keys), list, options?.threshold, options?.ignoreLocation]
  );

  return useMemo(() => {
    const q = query.trim();
    if (!q) return list;
    return fuse.search(q).map((r) => r.item);
  }, [fuse, list, query]);
}

/**
 * Non-hook version for use inside other useMemo blocks or plain functions.
 */
export function fuseFilter<T>(
  list: T[],
  keys: (string | { name: string; weight: number })[],
  query: string,
  options?: FuseSearchOptions
): T[] {
  const q = query.trim();
  if (!q) return list;
  const fuse = new Fuse(list, {
    keys: keys as any,
    threshold: options?.threshold ?? 0.4,
    ignoreLocation: options?.ignoreLocation ?? true,
    includeScore: false,
  });
  return fuse.search(q).map((r) => r.item);
}
